import { useBuy } from "@/context/buy";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Container from "../Common/Container";
import { decryptInteger, encryptInteger } from "@/Utils/encryptInteger";
import User from "@/Layouts/User";
import { useRouter } from "next/router";
import Head from "next/head";
import { formatShortDateTime } from "@/Utils/dateFormat";
import CheckoutForm from "./CheckoutForm";

export default function PaymentSuccessModal() {
    const router = useRouter();
    const stripe = useStripe();
    const [loading, setLoading] = useState(false)
    const [active, setActive] = useState(2);
    const [showData, setShowData] = useState(null)
    const [activateAccountData, setActivateAccountData] = useState(false)
    const [email, setEmail] = useState("")
    const [isPaymentFailed, setIsPaymentFailed] = useState(false);
    const [link, setLink] = useState("https://www.qoncertapp.com/");
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [stripeElements, setStripeElements] = useState(null);

    function formatDateInCustomTimezone(show_date, show_time) {
        return formatShortDateTime(show_date, show_time);
    }

    const getAppLink = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (/android/i.test(userAgent)) {
            return "https://play.google.com/store/apps/details?id=com.qoncertapp.artist";
        } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return "https://apps.apple.com/us/app/qoncert-live-shows/id1537042846";
        } else if (/Macintosh|Mac OS X/.test(userAgent)) {
            return "https://apps.apple.com/us/app/qoncert-live-shows/id1537042846";
        } else {
            return "https://www.qoncertapp.com/";
        }
    };

    useEffect(() => {
        setLink(getAppLink());
    }, []);

    useEffect(() => {

        setLoading(true);
        if (!stripe) {
            return;
        }

        setLoading(true);
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            if (!paymentIntent) {
                setLoading(false);
                setIsPaymentFailed(true);
                toast.error("Unable to retrieve payment status.");
                return;
            }

            const status = paymentIntent.status;
            const decrypted = decryptInteger(localStorage.getItem("temp_data"));

            switch (status) {
                case "succeeded": {
                    // Immediately show success and fetch show details once to populate UI
                    setIsPaymentFailed(false);
                    setLoading(true);
                    fetchPaymentStatus(JSON.parse(decrypted));
                    break;
                }
                case "processing":
                case "requires_capture":
                case "requires_confirmation": {
                    // Only these processing-like states should start polling
                    startPolling(JSON.parse(decrypted));
                    break;
                }
                case "requires_action": {
                    setLoading(false);
                    setIsPaymentFailed(true);
                    toast.error("Payment requires additional customer action.");
                    break;
                }
                case "requires_payment_method": {
                    setLoading(false);
                    setIsPaymentFailed(true);
                    toast.error("Payment requires a valid payment method.");
                    break;
                }
                case "canceled": {
                    setLoading(false);
                    setIsPaymentFailed(true);
                    toast.error("Payment was canceled.");
                    break;
                }
                default: {
                    setLoading(false);
                    setIsPaymentFailed(true);
                    toast.error("Unknown payment status.");
                }
            }
        });
    }, [stripe]);

    const POLLING_INTERVAL = 3000;
    const intervalIdRef = useRef(null);

    const retryCountRef = useRef(0);
    const MAX_RETRIES = 10;

    const startPolling = (data = []) => {
        if (intervalIdRef.current) return;

        intervalIdRef.current = setInterval(() => {
            if (retryCountRef.current >= MAX_RETRIES) {
                stopPolling();
                setLoading(false);
                setIsPaymentFailed(true);
                toast.error("Unable to confirm payment. Please contact support.");
                return;
            }
            retryCountRef.current++;
            fetchPaymentStatus(data);
        }, POLLING_INTERVAL);
    };

    const stopPolling = () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    };

    const fetchPaymentStatus = async (data = []) => {
        try {
            let payment_data = localStorage.getItem("payment_data");
            let decryptedPaymentData = null;
            
            try {
                if (payment_data) {
                    decryptedPaymentData = JSON.parse(decryptInteger(payment_data));
                }
            } catch (e) {
                console.warn("Could not decrypt payment_data, will use fallback:", e);
            }

            const formData = new FormData();
            formData.append("action", "getRTicketById");
            formData.append("reservation_id", decryptedPaymentData?.reservation_id || data?.id);

            let encryptedData = localStorage.getItem("user_data");
            let decryptedData = null;
            let web_token = null;
            
            try {
                if (encryptedData) {
                    decryptedData = JSON.parse(decryptInteger(encryptedData));
                }
                web_token = decryptedPaymentData?.web_token || decryptedData?.web_token;
            } catch (e) {
                console.warn("Could not decrypt user_data:", e);
            }
            
            // If no web_token available, skip API call and use fallback immediately
            if (!web_token) {
                console.log("No web_token available, using fallback to show success from localStorage");
                handleAuthErrorWithFallback(data, decryptedPaymentData);
                return;
            }

            const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`, {
                method: "POST",
                body: formData,
                headers: {
                    "Authorization": `Bearer ${web_token}`
                }
            });

            if (!response.ok) {
                console.error(`HTTP Error: ${response.status} ${response.statusText}`);
                return;
            }

            const resData = await response.json();

            if (resData.status) {
                let status = resData.data.payment_status;
                let message = "";

                switch (status) {
                    case 2:
                        message = "Payment Successful";
                        stopPolling();
                        if (resData.data.show?.id) {
                            getShowDetails(resData.data);
                        } else {
                            console.error("Show ID missing in response");
                            setLoading(false);
                            toast.error("Payment successful but unable to load show details.");
                        }
                        break;
                    case 3:
                        message = "Payment Failed";
                        setIsPaymentFailed(true);
                        stopPolling();
                        setLoading(false);
                        toast.error(message);
                        break;
                    case 0:
                        message = "Payment Pending";
                        break;
                    default:
                        message = "Unknown Payment Status";
                        stopPolling();
                        setLoading(false);
                        toast.error(message);
                        break;
                }

                // Set email if available
                if (resData.data.email) {
                    setEmail(resData.data.email);
                }
                if (decryptedPaymentData?.reservation_id) {
                    setActivateAccountData(true);
                }
            } else {
                const errorMessage = resData.message || "Unknown error occurred";
                const errorCode = resData.code;
                
                // Handle authentication error (code 201 = Invalid User Code) first
                // Check for both string and number format
                if (errorCode === 201 || errorCode === "201") {
                    stopPolling();
                    // Since Stripe says payment succeeded, try to show success using localStorage data
                    // Don't log as error since we're handling it gracefully
                    console.log("Authentication token invalid (code 201), using fallback to show success from localStorage");
                    handleAuthErrorWithFallback(data, decryptedPaymentData);
                    return;
                }
                
                // Log other errors (but don't spam console during polling)
                if (intervalIdRef.current === null) {
                    // Only log if not polling (i.e., direct call)
                    console.error("API returned error:", errorMessage, "Code:", errorCode);
                }
                
                // For other errors during polling, continue retrying
                // The max retry check will handle stopping after MAX_RETRIES
            }
        } catch (error) {
            console.error("Error processing payment status:", error);
        }
    };

    const handleAuthErrorWithFallback = async (data, decryptedPaymentData) => {
        try {
            // Since Stripe confirmed payment succeeded, try to show success using localStorage data
            setIsPaymentFailed(false);
            
            // Try to get show_id from various sources
            let showId = null;
            
            // Check if data parameter has show info
            if (data?.show?.id) {
                showId = data.show.id;
            } else if (data?.show_id) {
                showId = data.show_id;
            }
            
            // Check payment_data
            if (!showId && decryptedPaymentData?.show_id) {
                showId = decryptedPaymentData.show_id;
            }
            
            // Check temp_data as last resort
            if (!showId) {
                try {
                    const tempDataStr = localStorage.getItem("temp_data");
                    if (tempDataStr) {
                        const decryptedTemp = JSON.parse(decryptInteger(tempDataStr));
                        if (decryptedTemp?.show_id) {
                            showId = decryptedTemp.show_id;
                        } else if (decryptedTemp?.show?.id) {
                            showId = decryptedTemp.show.id;
                        }
                    }
                } catch (e) {
                    console.error("Error reading temp_data:", e);
                }
            }
            
            // If we found a show_id, fetch show details (this doesn't require auth)
            if (showId) {
                await getShowDetails({ show: { id: showId } });
                toast.success("Payment successful! Please log in to view full ticket details.");
            } else {
                // No show_id found, show generic success
                setLoading(false);
                toast.success("Payment successful! However, we couldn't load ticket details. Please log in to view your tickets.");
            }
            
            // Set email and activate account flag if available
            if (decryptedPaymentData?.email) {
                setEmail(decryptedPaymentData.email);
            }
            if (decryptedPaymentData?.reservation_id || data?.id) {
                setActivateAccountData(true);
            }
        } catch (error) {
            console.error("Error in handleAuthErrorWithFallback:", error);
            setLoading(false);
            toast.error("Payment succeeded, but there was an error loading details. Please log in to view your tickets.");
        }
    };

    const getShowDetails = async (data) => {
        try {
            let payment_data = localStorage.getItem("payment_data");

            const formData = new FormData();
            formData.append("action", "getShowDetail");
            formData.append("show_id", data.show.id);

            const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageShow`, {
                method: "POST",
                body: formData,
                headers: {
                    Accept: "application/json",
                },
            });


            const resData = await response.json();

            if (resData.status) {
                const showData = resData?.data || {};
                
                // Convert show_at to show_date and show_time if needed
                if (showData.show_at && (!showData.show_date || !showData.show_time)) {
                    try {
                        // Parse show_at (format: "YYYY-MM-DD HH:mm:ss")
                        const showAtStr = showData.show_at.toString().trim();
                        const [datePart, timePart] = showAtStr.split(' ');
                        
                        if (datePart && timePart) {
                            showData.show_date = datePart;
                            // Ensure time is in HH:MM:SS format
                            const timeParts = timePart.split(':');
                            if (timeParts.length >= 2) {
                                showData.show_time = `${timeParts[0]}:${timeParts[1]}:${timeParts[2] || '00'}`;
                            } else {
                                showData.show_time = timePart;
                            }
                        } else {
                            // Try parsing as ISO string
                            const dateObj = new Date(showAtStr);
                            if (!isNaN(dateObj.getTime())) {
                                const year = dateObj.getFullYear();
                                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                const day = String(dateObj.getDate()).padStart(2, '0');
                                const hours = String(dateObj.getHours()).padStart(2, '0');
                                const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                                const seconds = String(dateObj.getSeconds()).padStart(2, '0');
                                
                                showData.show_date = `${year}-${month}-${day}`;
                                showData.show_time = `${hours}:${minutes}:${seconds}`;
                            }
                        }
                    } catch (parseError) {
                        console.warn("Error parsing show_at:", parseError);
                    }
                }
                
                setShowData(showData);
                setLoading(false);
            } else {
                toast.error(resData.message || "Failed to load show details");
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching show details:", error);
            toast.error("Failed to load show details. Please try again.");
            setLoading(false);
        }
    };

    const sendMagicLink = async () => {
        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageUser`, {
                method: "POST",
                body: new URLSearchParams({
                    action: "sentMagicLink",
                    email: email,
                }),
            });
            const data = await response.json();
            if (data.status) {
                setEmail("")
                toast.success("Magic link sent successfully");
            } else {
                toast.error(data?.message || "Error in Login Please Try Again");
            }
            setTimeout(() => {
                router.push("/")
            }, 420)
        } catch (error) {
            console.log("error", error);
        }
    }

    const tryAgain = async () => {
        try {
            let payment_data = localStorage.getItem("payment_data");
            let decryptedPaymentData = JSON.parse(decryptInteger(payment_data));
            const decrypted = JSON.parse(decryptInteger(localStorage.getItem("temp_data")));

            let formData = new FormData();
            formData.append("action", "createPI");
            formData.append("reservation_id", decrypted?.id);

            let encryptedData = localStorage.getItem("user_data")
            let decryptedData = JSON.parse(decryptInteger(encryptedData));

            let web_token = decryptedPaymentData?.web_token || decryptedData?.web_token;

            const response = await fetch(
                `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        Authorization: `Bearer ${web_token}`,
                    },
                }
            );

            const resData = await response.json();

            if (resData.status) {
                const encryptedJson = encryptInteger(JSON.stringify(resData.data));
                localStorage.setItem("payment_data", encryptedJson);
                setPaymentLoading(true);
                loadStripePayment(resData.data);
            } else {
                toast.error(resData.message);
            }
        } catch (error) {
            console.error("Error in initiatePayment:", error);
        }
    }

    const loadStripePayment = async (data) => {
        try {
            const stripePromise = await loadStripe(data.publishableKey);

            const options = {
                clientSecret: data.paymentIntent,
                appearance: {
                    theme: "night",
                    variables: {
                        colorPrimary: "#FA2CB4",
                        colorBackground: "#0D0C0F",
                        colorText: "#FFFFFF",
                        colorDanger: "#ff3860",
                        borderRadius: "8px",
                    },
                },
                layout: {
                    type: "tabs",
                    defaultCollapsed: false,
                },
            };

            const elements = (
                <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm />
                </Elements>
            );

            setStripeElements(elements);
            setTimeout(() => {
                setPaymentLoading(false);
            }, 1000);
        } catch (error) {
            console.error("Error in loadStripePayment:", error);
        }
    };

    return (
        <User>
            <Head>
                <title>Qoncert App | Pyamnet Success!!</title>
            </Head>
            <Container className="">
                <div className="layout_bg_image hidden lg:block">
                    <span className="bg_inner_shadow"></span>
                </div>
                {stripeElements ? (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        className="flex justify-center  items-center fixed inset-0  overflow-y-auto bg-gray-700/50 z-[9999999]"
                    >
                        <div className="bg-[#0D0D10] h-fit w-[90%]  min-h-[300px]  md:mt-[50px] lg:mb-[50px] py-[36px] px-[18px] lg:p-12 rounded-lg md:w-1/3">
                            {paymentLoading ? (
                                <div className="flex h-[270px] w-full items-center justify-center">
                                    <div className="loader"></div>
                                </div>
                            ) : (
                                stripeElements
                            )}
                        </div>
                    </div>
                ) : (
                    <div id='showDetails' className=''>
                        <div id="LoginModal" className=" h-fit">
                            <div
                                className="flex flex-col md:flex-row justify-start lg:justify-center items-center relative z-[] overflow-y-auto h-full inset-0 px-2 lg:px-12 md:px-0  bg-opacity-70 "
                            >
                                <div
                                    id="ticket-modal"
                                    className="bg-[#17191d] h-fit w-full max-w-[728px] md:my-[60px] py-[28px] px-[18px]  lg:pt-14 lg:pb-[42px] rounded-[16px] lg:rounded-2xl "
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {loading ? (
                                        <div >
                                            <div className="flex h-[500px] flex-col items-center justify-center p-4">
                                                <div style={{ width: "200px", height: "200px" }}>
                                                    <div className='w-full flex justify-center items-center h-full'>
                                                        <div className="loader"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="md:px-12">
                                            <div className="hidden lg:block">
                                                <div className="breadcrumbs   max-w-full">
                                                    <div
                                                        className={`flex gap-4 items-center bread-item ${active == 0 ? "active-bread" : ""
                                                            }`}
                                                    >
                                                        <p>Account</p>
                                                    </div>
                                                    <div className="bread-icons gap-3 flex items-center">
                                                        <svg
                                                            className={` ${active == 0 ? "active-bread" : ""}`}
                                                            width="25"
                                                            height="1"
                                                            viewBox="0 0 25 1"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <line
                                                                y1="0.5"
                                                                x2="25"
                                                                y2="0.5"
                                                                stroke={`${active == 0 ? "#FA2CB4" : "#9E9E9E"} `}
                                                            />
                                                        </svg>
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <g clipPath="url(#clip0_1708_9606)">
                                                                <path
                                                                    d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                                                                    fill="#9E9E9E"
                                                                />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_1708_9606">
                                                                    <rect width="24" height="24" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <svg
                                                            width="25"
                                                            height="1"
                                                            viewBox="0 0 25 1"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <line
                                                                y1="0.5"
                                                                x2="25"
                                                                y2="0.5"
                                                                stroke={`${active == 1 ? "#FA2CB4" : "#9E9E9E"} `}
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        className={`flex gap-4 items-center bread-item ${active == 1 ? "active-bread" : ""
                                                            }`}
                                                    >
                                                        <p>Payment</p>
                                                    </div>
                                                    <div className="bread-icons gap-3 flex items-center">
                                                        <svg
                                                            width="25"
                                                            height="1"
                                                            viewBox="0 0 25 1"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <line
                                                                y1="0.5"
                                                                x2="25"
                                                                y2="0.5"
                                                                stroke={`${active == 1 ? "#FA2CB4" : "#9E9E9E"} `}
                                                            />
                                                        </svg>
                                                        <svg
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <g clipPath="url(#clip0_1708_9606)">
                                                                <path
                                                                    d="M12 22C6.47699 22 2 17.523 2 12C2 6.477 6.47699 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM11.003 16L18.073 8.929L16.659 7.515L11.003 13.172L8.17399 10.343L6.75999 11.757L11.003 16Z"
                                                                    fill="#9E9E9E"
                                                                />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_1708_9606">
                                                                    <rect width="24" height="24" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <svg
                                                            width="25"
                                                            height="1"
                                                            viewBox="0 0 25 1"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <line
                                                                y1="0.5"
                                                                x2="25"
                                                                y2="0.5"
                                                                stroke={`${active == 2 ? "#FA2CB4" : "#9E9E9E"} `}
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div
                                                        className={`flex gap-4 items-center bread-item ${active == 2 ? "active-bread" : ""
                                                            }`}
                                                    >
                                                        <p>Confirmation</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="lg:mt-11 createeAcc">
                                                <p className={`text-lg font-[700] lg:text-[26px] ${isPaymentFailed ? "text-[#f54435] " : ""}`}>{isPaymentFailed ? "Oops, Payment Failed!" : "Ticket Confirmed! 🎉"}</p>
                                                <div className="mt-6 lg:mt-14">
                                                    <div className="ticket-card">
                                                        <div className="flex gap-4 items-center h-full">
                                                            {/* Show Photo/Banner */}
                                                            <div className="ticket-main-image aspect-square max-h-[103px] lg:max-h-max ">
                                                                {showData?.banner ? (
                                                                    <img 
                                                                        src={showData.banner} 
                                                                        alt={showData?.name || "Show banner"} 
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full bg-[#1F222A] flex items-center justify-center text-[#9E9E9E] text-sm">
                                                                        No Image
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col pt-5 gap-4 h-full">
                                                                {/* Show Name */}
                                                                <p className="modal-header urbanist">
                                                                    {showData?.name || "Show Name"}
                                                                </p>
                                                                
                                                                {/* Date and Time */}
                                                                {showData?.show_date && showData?.show_time ? (
                                                                    <p className="modal-date text-lg urbanist">
                                                                        {formatDateInCustomTimezone(showData.show_date, showData.show_time)}
                                                                    </p>
                                                                ) : showData?.show_at ? (
                                                                    <p className="modal-date text-lg urbanist">
                                                                        {(() => {
                                                                            try {
                                                                                const date = new Date(showData.show_at);
                                                                                if (!isNaN(date.getTime())) {
                                                                                    return date.toLocaleDateString('en-US', {
                                                                                        weekday: 'short',
                                                                                        month: 'short',
                                                                                        day: 'numeric',
                                                                                        year: 'numeric',
                                                                                        hour: 'numeric',
                                                                                        minute: '2-digit',
                                                                                        hour12: true
                                                                                    });
                                                                                }
                                                                            } catch (e) {
                                                                                console.warn("Error formatting show_at:", e);
                                                                            }
                                                                            return "Date and time to be announced";
                                                                        })()}
                                                                    </p>
                                                                ) : (
                                                                    <p className="modal-date text-lg urbanist text-[#9E9E9E]">
                                                                        Date and time to be announced
                                                                    </p>
                                                                )}
                                                                
                                                                {/* City and State */}
                                                                <div className="modal-location urbanist">
                                                                    <svg
                                                                        width="16"
                                                                        height="17"
                                                                        viewBox="0 0 16 17"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            clipRule="evenodd"
                                                                            d="M2.33301 7.823C2.33301 4.75643 4.8956 2.27783 7.99531 2.27783C11.1038 2.27783 13.6663 4.75643 13.6663 7.823C13.6663 9.36829 13.1043 10.8029 12.1793 12.0189C11.1589 13.3602 9.90111 14.5288 8.48537 15.4461C8.16135 15.6581 7.86892 15.6741 7.51331 15.4461C6.0895 14.5288 4.83173 13.3602 3.82001 12.0189C2.89433 10.8029 2.33301 9.36829 2.33301 7.823ZM6.12916 7.99567C6.12916 9.02297 6.96745 9.83095 7.9953 9.83095C9.02384 9.83095 9.87019 9.02297 9.87019 7.99567C9.87019 6.97636 9.02384 6.12905 7.9953 6.12905C6.96745 6.12905 6.12916 6.97636 6.12916 7.99567Z"
                                                                            fill="white"
                                                                        />
                                                                    </svg>
                                                                    <span className="modal-date text-lg ">
                                                                        {showData?.venue?.city && showData?.venue?.state ? (
                                                                            `${showData.venue.city}, ${showData.venue.state}`
                                                                        ) : showData?.venue?.city ? (
                                                                            showData.venue.city
                                                                        ) : showData?.venue?.state ? (
                                                                            showData.venue.state
                                                                        ) : (
                                                                            <span className="text-[#9E9E9E]">Location TBA</span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {activateAccountData ? (
                                                    <>
                                                        <p className="mt-6 font-[700] text-lg lg:mt-11">Activate Your Qoncert Account Now</p>
                                                        <ul className="mt-7 flex flex-col gap-1 text-[#c9c9c9] ">
                                                            <li className="flex items-start gap-2"> <span className="text-[60px] leading-[16px] ">•</span>  View your tickets</li>
                                                            <li className="flex items-start gap-2"> <span className="text-[60px] leading-[16px] ">•</span> Get Show updates</li>
                                                            <li className="flex items-start gap-2"> <span className="text-[60px] leading-[16px] ">•</span> Earn Rewards</li>
                                                        </ul>
                                                        <div className="mt-11">
                                                            <p>We&apos;ll send you a magic link to log in anytime.</p>
                                                            <p>No password to remember.</p>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="mt-6 font-[700] text-lg lg:mt-11">Next Steps</p>
                                                        {isPaymentFailed ? (

                                                            <p className="mt-3 lg:mt-6 text-base lg:text-lg">You&apos;re Payment Failed Let&apos;s Try Again</p>
                                                        ) : (
                                                            <>
                                                                <p className="mt-6 lg:mt-11 text-base lg:text-lg">Check your email for a copy of your ticket</p>
                                                                <p className="mt-3 lg:mt-6 text-base lg:text-lg">Download the <a href={link} target='_blank' className='text-[#FA2CB4]'>Qoncert app</a>  to access your account</p>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                            <div className="mt-5 lg:mt-12 pt-1 relative">
                                                <div className="absolute w-[120%] left-[-10%]">
                                                    <svg
                                                        width="100%"
                                                        height="2"
                                                        viewBox="0 0 694 2"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <line y1="1" x2="694.001" y2="1" stroke="#332D41" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div
                                                className={`mt-5 lg:mt-8 modal-footer flex  gap-8 items-center ${activateAccountData ? "justify-between" : "justify-center"} `}
                                            >
                                                {isPaymentFailed && (
                                                    <button
                                                        onClick={() => {
                                                            tryAgain();
                                                        }}
                                                        className=" border-[#F129BA] bg-[#F129BA] font-urbanist font-[500] border text-[14px] lg:text-lg py-[10px] lg:py-[19px] lg:leading-[10px] px-[62px] w-[340px] rounded-[10px] "
                                                    >
                                                        Try Again
                                                    </button>
                                                )}

                                                {activateAccountData ? (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                router.push('/')
                                                            }}
                                                            className={`w-[320px] border-2  border-[#1F222A] py-3 font-[700] rounded-[16px] no-selection `}
                                                        >
                                                            {" "}
                                                            Skip for now
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                sendMagicLink();
                                                            }}
                                                            className={`w-[320px] main_btn_bg py-3 font-[700] rounded-[16px] no-selection `}
                                                        >
                                                            {" "}
                                                            Activate Account
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            router.push(`/dashboard`);
                                                        }}
                                                        className={`border-[#35383F] bg-[#6b6b6b17] font-urbanist font-[500] border text-[14px] lg:text-lg py-[10px] lg:py-[19px] lg:leading-[10px] px-[62px] w-[340px] rounded-[10px]  `}
                                                    >
                                                        {" "}
                                                        Close
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Container>
        </User>
    )
}
